/**
 * Shared RFP note hooks
 *
 * These hooks provide React Query integration for BTX RFP-level note endpoints.
 * They support cache sharing across feature libraries (PlanDesigner, SendRfpDrawer).
 *
 * Uses BTX endpoints:
 * - GET /btx/v1/rfps/{rfpId}/note
 * - PUT /btx/v1/rfps/{rfpId}/note
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult,
} from "@tanstack/react-query";
import { toMinutes, useApi } from "@platform/api";
import { logger } from "@platform/browser-logger";

import { RfpNotePayload } from "./types";

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query keys for RFP note caching.
 *
 * Uses the same key shape as the PlanDesigner so both libraries share the
 * React Query cache automatically.
 */
export const rfpNoteQueryKeys = {
  rfpNote: (rfpId: string): ["rfp-notes", string] =>
    ["rfp-notes", rfpId] as const,
};

// ============================================================================
// Client Functions
// ============================================================================

/**
 * Fetches the RFP-level note.
 *
 * Returns `{ content: "" }` on 404 (no note saved yet).
 */
const getRfpNote = async (
  rfpId: string,
  axiosInstance: { get: <T>(url: string) => Promise<{ data: T }> },
): Promise<RfpNotePayload> => {
  try {
    const response = await axiosInstance.get<RfpNotePayload>(
      `btx/v1/rfps/${rfpId}/note`,
    );
    return response.data;
  } catch (error: unknown) {
    // Treat 404 as "no note" rather than an error
    if (
      typeof error === "object" &&
      error !== null &&
      "response" in error &&
      (error as { response?: { status?: number } }).response?.status === 404
    ) {
      return { content: "" };
    }
    throw error;
  }
};

/**
 * Creates or updates the RFP-level note.
 */
const upsertRfpNote = async (
  rfpId: string,
  request: RfpNotePayload,
  axiosInstance: {
    put: <T>(url: string, data: unknown) => Promise<{ data: T }>;
  },
): Promise<RfpNotePayload> => {
  const response = await axiosInstance.put<RfpNotePayload>(
    `btx/v1/rfps/${rfpId}/note`,
    request,
  );
  return response.data;
};

// ============================================================================
// Query Hooks
// ============================================================================

interface UseRfpNoteOptions {
  ecoRfpId: string;
  enabled?: boolean;
}

/**
 * Hook to fetch the RFP-level note
 *
 * @param options.ecoRfpId - The BTX (eco) RFP ID
 * @param options.enabled - Whether the query should execute (default: true)
 * @returns Query result with RFP note data
 *
 * @example
 * ```tsx
 * const { data: rfpNote } = useRfpNote({ ecoRfpId: "eco-123" });
 * console.log(rfpNote?.content);
 * ```
 */
export const useRfpNote = ({
  ecoRfpId,
  enabled = true,
}: UseRfpNoteOptions): UseQueryResult<RfpNotePayload, Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: rfpNoteQueryKeys.rfpNote(ecoRfpId),
    queryFn: () => getRfpNote(ecoRfpId, axiosInstance),
    enabled: enabled && !!ecoRfpId,
    staleTime: toMinutes(5),
  });
};

// ============================================================================
// Mutation Hooks
// ============================================================================

interface UseUpsertRfpNoteOptions {
  ecoRfpId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to upsert the RFP-level note
 *
 * Updates the cache after a successful upsert.
 *
 * @param options.ecoRfpId - The BTX (eco) RFP ID
 * @param options.onSuccess - Callback on successful upsert
 * @param options.onError - Callback on error
 * @returns Mutation result accepting `{ content: string }`
 *
 * @example
 * ```tsx
 * const { mutate: saveRfpNote } = useUpsertRfpNote({ ecoRfpId: "eco-123" });
 * saveRfpNote({ content: "Updated RFP note" });
 * ```
 */
export const useUpsertRfpNote = ({
  ecoRfpId,
  onSuccess,
  onError,
}: UseUpsertRfpNoteOptions): UseMutationResult<
  RfpNotePayload,
  Error,
  RfpNotePayload
> => {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: RfpNotePayload) =>
      upsertRfpNote(ecoRfpId, request, axiosInstance),
    onSuccess: (_data, { content }) => {
      queryClient.setQueryData<RfpNotePayload>(
        rfpNoteQueryKeys.rfpNote(ecoRfpId),
        { content },
      );
      logger.debug("[useUpsertRfpNote] Successfully saved RFP note", {
        ecoRfpId,
      });
      onSuccess?.();
    },
    onError: (error) => {
      logger.error("Failed to upsert RFP note", error, { ecoRfpId });
      onError?.(error);
    },
  });
};
